// Karofi site header (sections/k-header.liquid) and footer (sections/k-footer.liquid).

class KHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('.k-drawer');
    this.burger = this.querySelector('[data-k-open]');
    this.menuButtons = [...this.querySelectorAll('.k-nav__link[aria-controls]')];

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
    this.updateHeight();
    window.addEventListener('resize', () => this.updateHeight(), { passive: true });

    this.setupDropdowns();
    this.setupProductMenus();
    this.burger?.addEventListener('click', () => this.openDrawer());
    this.querySelectorAll('[data-k-close]').forEach((el) => el.addEventListener('click', () => this.closeDrawer()));
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (this.drawer && !this.drawer.hidden) this.closeDrawer();
      this.closeMenus(true);
    });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.k-nav__item')) this.closeMenus();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll() {
    this.classList.toggle('is-scrolled', window.scrollY > 8);
  }

  // Lets other components (e.g. sticky product media) offset below the header.
  updateHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }

  setupDropdowns() {
    const hoverable = window.matchMedia('(hover: hover)');
    this.menuButtons.forEach((button) => {
      const item = button.closest('.k-nav__item');
      let closeTimer;
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        this.closeMenus();
        button.setAttribute('aria-expanded', String(open));
      });
      item.addEventListener('mouseenter', () => {
        if (!hoverable.matches) return;
        clearTimeout(closeTimer);
        this.closeMenus();
        button.setAttribute('aria-expanded', 'true');
      });
      item.addEventListener('mouseleave', () => {
        if (!hoverable.matches) return;
        closeTimer = setTimeout(() => button.setAttribute('aria-expanded', 'false'), 150);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) button.setAttribute('aria-expanded', 'false');
      });
    });
  }

  // Full-width product menu: hovering or focusing a collection shows its products.
  setupProductMenus() {
    this.querySelectorAll('[data-k-pmenu]').forEach((menu) => {
      const links = [...menu.querySelectorAll('[data-k-panel]')];
      const panels = [...menu.querySelectorAll('[data-k-panel-body]')];
      const show = (link) => {
        links.forEach((other) => other.setAttribute('aria-current', String(other === link)));
        panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.kPanelBody === link.dataset.kPanel));
      };
      links.forEach((link) => {
        link.addEventListener('mouseenter', () => show(link));
        link.addEventListener('focus', () => show(link));
      });
    });
  }

  closeMenus(returnFocus = false) {
    this.menuButtons.forEach((button) => {
      if (button.getAttribute('aria-expanded') === 'true' && returnFocus) button.focus();
      button.setAttribute('aria-expanded', 'false');
    });
  }

  openDrawer() {
    this.drawer.hidden = false;
    this.burger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('k-lock');
    requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
      this.drawer.querySelector('.k-drawer__panel').focus();
    });
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    this.burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('k-lock');
    const panel = this.drawer.querySelector('.k-drawer__panel');
    const hide = () => {
      this.drawer.hidden = true;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) hide();
    else panel.addEventListener('transitionend', hide, { once: true });
    this.burger.focus();
  }
}

if (!customElements.get('k-header')) customElements.define('k-header', KHeader);

// Footer menus collapse into accordions on phones.
const footerMedia = window.matchMedia('(max-width: 749px)');
const syncFooter = () => {
  document.querySelectorAll('.k-footer__col[open], details.k-footer__col').forEach((details) => {
    if (details.tagName === 'DETAILS') details.open = !footerMedia.matches;
  });
};
syncFooter();
footerMedia.addEventListener('change', syncFooter);
